import RouteTemplate from 'ember-route-template'
import type ReStudentSummaryTableRoute from 'teacher-ui-client/reporting/ui-scope/re/student-summary/table/route'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingStudentSummaryControls } from 'teacher-ui-client/components/reporting/student-summary-controls'
import { StudentDashboardTile } from 'teacher-ui-client/components/tiles/student-dashboard'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import CalendarIconCell from 'teacher-ui-client/components/stable-table/basic-cells/calendar-icon'
import TooltipCell from 'teacher-ui-client/components/stable-table/basic-cells/tooltip'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import { t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { unixTimeToCalendarIconArgs } from 'teacher-ui-client/utils/unix-time-to-calendar-icon'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { reportingFilename } from 'teacher-ui-client/utils/filename'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type { IntlService } from 'ember-intl'
import type TableToCsv from 'teacher-ui-client/services/table-to-csv'
import type { SafeString } from '@ember/template'
import { isPresent } from '@ember/utils'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type {
  AssignmentsRecentActivityItemRe,
  AssignmentsRecentActivityDrivingTestItemRe,
} from 'teacher-ui-client/utils/student-events/fetchers/summary'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'

interface Signature {
  Args: {
    model: ModelFor<ReStudentSummaryTableRoute>
  }
}

export class ReStudentSummaryTablePage extends Component<Signature> {
  @service intl!: IntlService
  @service tableToCsv!: TableToCsv
  @service lessonInfo!: LessonInfoService

  readonly tileTheme = ThemeKeys.blue

  get pageTitle() {
    const studentName = this.args.model.student.fullName
    return `Student results ${studentName}`
  }

  get summary() {
    return this.args.model.summary?.[0]
  }

  get emptyText() {
    return this.intl.t('reporting.re.studentSummary.emptyText')
  }

  get lexileGrowthData() {
    const highestText = this.intl.t('reporting.re.studentSummary.lexileLevel.highestLexile')
    const growthText = this.intl.t('reporting.re.studentSummary.lexileLevel.lexileGrowth')
    const columns = [
      { value: highestText, cellClassNames: 'py-3 text-center' },
      { value: growthText, cellClassNames: 'py-3 text-center' },
    ]

    const rows = [
      {
        cells: [
          { value: formatValue(this.summary?.lexiles.highest) },
          { value: formatValue(this.summary?.lexiles.growth, { prepend: '+' }) },
        ],
      },
    ]

    return { columns, rows }
  }

  get readingLessonsProgressData() {
    const columns = [{ cellClassNames: 'p-3 text-left' }, { cellClassNames: 'w-24 text-center' }]

    const {
      initial_lesson: initial,
      highest_lesson: highest,
      total_lessons: total,
      total_lessons_mastered: totalMastered,
    } = this.summary?.reading_lessons ?? {}

    const initialText = this.intl.t('reporting.re.studentSummary.readingLessons.initialLesson')
    const highestText = this.intl.t('reporting.re.studentSummary.readingLessons.highestLesson')
    const totalText = this.intl.t('reporting.re.studentSummary.readingLessons.totalLessons')
    const totalMasteredText = this.intl.t('reporting.re.studentSummary.readingLessons.totalLessonsMastered')

    const totalMasteredTooltip = this.intl.t('reporting.re.studentSummary.readingLessons.totalLessonsMasteredTooltip')

    const rows = [
      { cells: [{ value: initialText }, { value: formatValue(initial) }] },
      { cells: [{ value: highestText }, { value: formatValue(highest) }] },
      { cells: [{ value: totalText }, { value: formatValue(total) }], classNames: 'font-semibold' },
      {
        cells: [
          {
            component: TooltipCell,
            classNames: 'bg-oceany-blue-100 border-r border-white',
            value: totalMasteredText,
            tooltipValue: totalMasteredTooltip,
          },
          { value: formatValue(totalMastered), classNames: 'bg-oceany-blue-100' },
        ],
        classNames: 'font-semibold',
      },
    ]

    return { columns, rows }
  }

  get readingLessonsPlacementTestData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
    ]

    const readingPlacementTests = this.summary?.reading_placement_tests || []

    const rows = readingPlacementTests.map((test) => {
      const calendarCell = unixTimeToCalendarIconArgs(test.created_at, this.intl)
      const isLessonSkipped = test.skipped_test
      const lessonNumber = test.lesson

      const lessonName = this.getLessonTextFromNumber(lessonNumber, 'lessons')
      const lessonSkippedText = this.intl.t('reporting.placementTest.skipped')

      const lessonText = isLessonSkipped ? lessonSkippedText : lessonName

      return {
        cells: [calendarCell, { value: lessonText }],
      }
    })

    return { columns, rows }
  }

  get readingLessonsActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const lessonsRecentActivity = this.summary?.reading_lessons?.recent_activity || []

    const rows = lessonsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const lessonNumber = activity.lesson
      const lessonText = this.getLessonTextFromNumber(lessonNumber, 'lessons')

      return {
        cells: [calendarCell, { value: lessonText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })
    return { columns, rows }
  }

  get readingAreasOfWeaknessRecentActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const areasOfWeaknessRecentActivity = this.summary?.reading_lessons?.areas_of_weakness || []

    const rows = areasOfWeaknessRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const lessonNumber = activity.lesson
      const lessonText = this.getLessonTextFromNumber(lessonNumber, 'lessons')

      return {
        cells: [calendarCell, { value: lessonText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  get booksReadProgressData() {
    const booksReadText = this.intl.t('reporting.re.studentSummary.booksRead.booksRead')
    const wordsReadText = this.intl.t('reporting.re.studentSummary.booksRead.wordsRead')

    const columns = [
      { cellClassNames: 'p-3 text-left' },
      { value: booksReadText, cellClassNames: 'p-3 text-center' },
      { value: wordsReadText, cellClassNames: 'p-3 text-center' },
    ]

    const books = this.summary?.books

    const genres = ['fiction', 'nonfiction'] as const

    const rows = genres.map((genre) => {
      const genreText = this.intl.t(`reporting.re.studentSummary.booksRead.${genre}`)

      const booksGenreData = books?.[genre]
      const booksRead = booksGenreData?.books_read
      const wordsRead = booksGenreData?.words_read

      const booksReadFormatted = formatValue(booksRead)

      // Format for thousands
      let wordsReadFormatted: string | SafeString = ''

      if (typeof wordsRead === 'undefined') {
        wordsReadFormatted = formatValue(wordsRead)
      } else if (wordsRead) {
        const isUnder1000 = wordsRead < 1000
        const wordsReadThousandFormatted = `${(wordsRead / 1000).toFixed(2)}k` // eg 1.26k
        wordsReadFormatted = isUnder1000 ? String(wordsRead) : wordsReadThousandFormatted
      }

      return { cells: [{ value: genreText }, { value: booksReadFormatted }, { value: wordsReadFormatted }] }
    })

    return { columns, rows }
  }

  get booksReadActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-32 text-center' },
    ]

    const booksReadRecentActivity = this.summary?.books?.recent_activity || []

    const rows = booksReadRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)

      const genre = activity.category // fiction || nonfiction
      const genreText = this.intl.t(`reporting.re.studentSummary.booksRead.${genre}`)
      const bookName = activity.book_name
      const activityText = `${genreText}: ${bookName}`

      const lexile = formatValue(activity.lexile)

      return {
        cells: [calendarCell, { value: activityText }, { value: lexile }],
      }
    })

    return { columns, rows }
  }

  get spellingLessonsProgressData() {
    const columns = [{ cellClassNames: 'p-3 text-left' }, { cellClassNames: 'w-24 text-center' }]

    const {
      highest_lesson: highest,
      total_lessons: total,
      average_score: average,
    } = this.summary?.spelling_lessons ?? {}

    const highestText = this.intl.t('reporting.re.studentSummary.spellingLessons.highestLesson')
    const totalText = this.intl.t('reporting.re.studentSummary.spellingLessons.totalLessons')
    const averageText = this.intl.t('reporting.re.studentSummary.spellingLessons.averageScore')

    const rows = [
      { cells: [{ value: highestText }, { value: formatValue(highest) }] },
      { cells: [{ value: totalText }, { value: formatValue(total) }] },
      { cells: [{ value: averageText }, { value: formatValue(average, { append: '%' }) }] },
    ]

    return { columns, rows }
  }

  get spellingLessonsPlacementTestData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
    ]

    const spellingPlacementTests = this.summary?.spelling_placement_tests ?? []

    const rows = spellingPlacementTests.map((test) => {
      const calendarCell = unixTimeToCalendarIconArgs(test.created_at, this.intl)
      const isLessonSkipped = test.skipped_test
      const lessonNumber = test.lesson

      const lessonName = this.getLessonTextFromNumber(lessonNumber, 'spelling')
      const lessonSkippedText = this.intl.t('reporting.placementTest.skipped')

      const lessonText = isLessonSkipped ? lessonSkippedText : lessonName

      return {
        cells: [calendarCell, { value: lessonText }],
      }
    })

    return { columns, rows }
  }

  get spellingLessonsActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
    ]

    const lessonsRecentActivity = this.summary?.spelling_lessons?.recent_activity ?? []

    const rows = lessonsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const lessonNumber = activity.lesson
      const lessonText = this.getLessonTextFromNumber(lessonNumber, 'spelling')

      return {
        cells: [calendarCell, { value: lessonText }],
      }
    })

    return { columns, rows }
  }

  get certificatesEarnedData() {
    const goldText = this.intl.t('reporting.re.studentSummary.certificatesEarned.gold')
    const silverText = this.intl.t('reporting.re.studentSummary.certificatesEarned.silver')
    const bronzeText = this.intl.t('reporting.re.studentSummary.certificatesEarned.bronze')
    const columns = [
      { value: goldText, cellClassNames: 'py-3 text-center' },
      { value: silverText, cellClassNames: 'py-3 text-center' },
      { value: bronzeText, cellClassNames: 'py-3 text-center' },
    ]

    const { gold, silver, bronze } = this.summary?.awards ?? {}

    const rows = [
      { cells: [{ value: formatValue(gold) }, { value: formatValue(silver) }, { value: formatValue(bronze) }] },
    ]

    return { columns, rows }
  }

  get mapQuizzesProgressData() {
    const columns = [{ cellClassNames: 'p-3 text-left' }, { cellClassNames: 'w-24 text-center' }]

    const { quizzes_taken: quizzes, average_score: average, attempts } = this.summary?.assessments ?? {}

    const numberQuizzesTaken = this.intl.t('reporting.re.studentSummary.mapQuizzes.numberQuizzesTaken')
    const averageQuizScore = this.intl.t('reporting.re.studentSummary.mapQuizzes.averageQuizScore')
    const totalAttempts = this.intl.t('reporting.re.studentSummary.mapQuizzes.totalAttempts')

    const rows = [
      { cells: [{ value: numberQuizzesTaken }, { value: formatValue(quizzes) }] },
      {
        classNames: 'bg-oceany-blue-100 font-semibold',
        cells: [
          { classNames: 'border-r border-white', value: averageQuizScore },
          { value: formatValue(average, { append: '%' }) },
        ],
      },
      { classNames: 'font-semibold', cells: [{ value: totalAttempts }, { value: formatValue(attempts) }] },
    ]

    return { columns, rows }
  }

  get mapQuizzesRecentActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const mapQuizzesRecentActivity = this.summary?.assessments?.recent_activity ?? []

    const rows = mapQuizzesRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const mapText = this.intl.t('reporting.re.studentSummary.mapQuizzes.activityRowQuiz', {
        mapNumber: activity.map_number,
      })
      const lessonRange = this.lessonInfo
        .lessonsFor(Precinct.RE_READING)
        .filter((lesson) => lesson.map === activity.map_number)
        .map((lesson) => lesson.lesson_position)
        .filter(isPresent)

      let assessingLessons = ''
      if (lessonRange && lessonRange.length > 0) {
        const lessonMin = lessonRange[0]
        const lessonMax = lessonRange[lessonRange.length - 1]
        assessingLessons = this.intl.t('reporting.re.studentSummary.mapQuizzes.activityRowLessons', {
          lessonMin,
          lessonMax,
        })
      }

      const activityText = `${mapText}: ${assessingLessons}`

      return {
        cells: [calendarCell, { value: activityText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  get drivingTestsProgressData() {
    const sightWordsText = this.intl.t('reporting.re.studentSummary.drivingTests.categories.sight_words')
    const lettersAndSoundsText = this.intl.t('reporting.re.studentSummary.drivingTests.categories.letters_and_sounds')
    const contentWordsText = this.intl.t('reporting.re.studentSummary.drivingTests.categories.content_words')
    const totalsText = this.intl.t('reporting.re.studentSummary.drivingTests.categories.total')

    const columns = [
      { value: sightWordsText, cellClassNames: 'py-3 text-center' },
      { value: lettersAndSoundsText, cellClassNames: 'py-3 text-center' },
      { value: contentWordsText, cellClassNames: 'py-3 text-center' },
      { value: totalsText, cellClassNames: 'py-3 text-center' },
    ]

    const totalsByCategory = this.summary?.driving_tests.totals_by_category
    const sightWords = totalsByCategory?.find((categoryData) => categoryData.category === 'sight_words')
    const lettersAndSounds = totalsByCategory?.find((categoryData) => categoryData.category === 'letters_and_sounds')
    const contentWords = totalsByCategory?.find((categoryData) => categoryData.category === 'content_words')

    const totalValue = totalsByCategory
      ? totalsByCategory.map((categoryData) => categoryData.completed || 0).reduce((sum, value) => sum + value, 0)
      : 0

    const rows = [
      {
        cells: [
          { value: formatValue(sightWords?.completed, { replacement: '0' }) },
          { value: formatValue(lettersAndSounds?.completed, { replacement: '0' }) },
          { value: formatValue(contentWords?.completed, { replacement: '0' }) },
          { value: totalValue },
        ],
      },
    ]

    return { columns, rows }
  }

  get drivingTestsActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const drivingTestsRecentActivity = this.summary?.driving_tests?.recent_activity || []

    const rows = drivingTestsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)

      const quizNumber = activity.quiz
      const categoryText = this.intl.t(`reporting.re.studentSummary.drivingTests.categories.${activity.category}`)
      const quizText = this.intl.t('reporting.re.studentSummary.drivingTests.activityRowQuiz', { quizNumber })
      const activityText = `${categoryText}: ${quizText}`

      let score
      if (activity.correct && activity.total) {
        const correct = Number(activity.correct)
        const total = Number(activity.total)
        if (Number.isInteger(correct) && Number.isInteger(total) && correct <= total && total > 0) {
          score = Math.round((correct / total) * 100)
        }
      }

      return {
        cells: [calendarCell, { value: activityText }, { value: formatValue(score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  get storylandsProgressData() {
    const columns = [{ cellClassNames: 'p-3 text-left' }, { cellClassNames: 'w-24 text-center' }]

    const { highest_lesson: highest, total_lessons: total } = this.summary?.storylands ?? {}

    const highestLesson = this.intl.t('reporting.re.studentSummary.storylands.highestLesson')
    const totalLessons = this.intl.t('reporting.re.studentSummary.storylands.totalLessons')

    const rows = [
      { cells: [{ value: highestLesson }, { value: formatValue(highest, { replaceZero: true }) }] },
      { cells: [{ value: totalLessons }, { value: formatValue(total) }] },
    ]

    return { columns, rows }
  }

  get storylandsRecentActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
    ]

    const storylandsRecentActivity = this.summary?.storylands?.recent_activity ?? []

    const rows = storylandsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const lessonNumber = activity.lesson
      const { score } = activity

      const activityText = this.intl.t('reporting.re.studentSummary.storylands.activityRowText', {
        lessonNumber,
        score,
      })

      return {
        cells: [calendarCell, { value: activityText }],
      }
    })

    return { columns, rows }
  }

  get hasAssignmentsData() {
    return isPresent(this.assignmentsData.rows)
  }

  get assignmentsData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const assignmentsRecentActivity = this.summary?.assignments?.recent_activity || []

    const rows = assignmentsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const assignmentText = this.formatAssignmentRow(activity)

      return {
        cells: [calendarCell, { value: assignmentText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  get hasStoriesWrittenActivityData() {
    return isPresent(this.storiesWrittenActivityData)
  }

  get storiesWrittenActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-oceany-blue-300',
        cellComponent: CalendarIconCell,
      },
      {},
      {},
      { cellClassNames: 'w-32 text-center' },
    ]

    const storiesWrittenRecentActivity = this.summary?.stories_written ?? []

    const rows = storiesWrittenRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)

      const storyTitle = activity.title
      const storyContest = activity.contest_name
      const storyDownloadUrl = join(
        config.readingEggsBaseUrl,
        '/students/',
        this.args.model.student.id,
        '/story_factory_entries/',
        `${activity.story_id}.pdf`,
      ).href

      const storyTitleText = this.intl.t('reporting.re.studentSummary.storiesWritten.storyTitleText', { storyTitle })
      const storyContestText = this.intl.t('reporting.re.studentSummary.storiesWritten.storyContestText', {
        storyContest,
      })
      const storyDownloadText = this.intl.t('reporting.re.studentSummary.storiesWritten.storyDownloadText')

      return {
        cells: [
          calendarCell,
          { value: storyTitleText },
          { value: storyContestText },
          { storyDownloadText, storyDownloadUrl },
        ],
      }
    })

    return { columns, rows }
  }

  private getLessonTextFromNumber(lessonPos: number, precinctStr: string) {
    const lessonText = `Lesson ${lessonPos}`

    let precinct: Precinct
    switch (precinctStr) {
      case 'lessons':
        precinct = Precinct.RE_READING
        break
      case 'spelling':
        precinct = Precinct.RE_SPELLING
        break
      case 'clinker_castle':
        precinct = Precinct.RE_STORYLANDS
        break
      default:
        return lessonText
    }
    const infoItem = this.lessonInfo.lessonsFor(precinct).find((infoItem) => infoItem.lesson_position === lessonPos)
    const lessonName = infoItem?.name
    return lessonName ? `${lessonText}: ${lessonName}` : lessonText
  }

  private formatAssignmentRow(
    assignment: AssignmentsRecentActivityItemRe | AssignmentsRecentActivityDrivingTestItemRe,
  ) {
    const { category: precinct, quiz, lesson } = assignment

    if (precinct === 'driving_tests') {
      const drivingTestCategory = 'driving_test_category' in assignment ? assignment.driving_test_category : ''
      const categoryName = this.intl.t(`driving_tests.re.categories.${drivingTestCategory}`)

      const lessonText = `${categoryName} Lesson ${quiz}`

      const infoItem = this.lessonInfo
        .lessonsFor(Precinct.RE_DRIVING_TESTS)
        .find((infoItem) => infoItem.lesson_position === quiz && infoItem.category === drivingTestCategory)
      const lessonName = infoItem?.name
      return lessonName ? `${lessonText}: ${lessonName}` : lessonText
    } else {
      // lesson should be a number here (only null for driving_tests)
      return typeof lesson === 'number' ? this.getLessonTextFromNumber(lesson, precinct) : ''
    }
  }

  get exportOptions() {
    const filename = `${reportingFilename(
      'reading-eggs-student-summary',
      this.args.model.student.fullName,
      this.args.model.period,
    )}.csv`
    return [
      {
        exportType: ExportType.CSV,
        exportAction: () => this.tableToCsv.downloadCsvFrom('[exportable]:not([exportable=false])', { filename }),
      },
      { exportType: ExportType.print },
    ]
  }

  get exportTitles() {
    const { intl } = this
    const t = (key: string) => intl.t(`reporting.re.studentSummary.${key}`)
    return {
      lexileGrowth: {
        progress: `${t('lexileLevel.title')} - ${t('lexileLevel.progressTitle')}`,
      },
      readingLessons: {
        progress: `${t('readingLessons.title')} - ${t('readingLessons.progressTitle')}`,
        placement: `${t('readingLessons.title')} - ${t('readingLessons.placementTestsTitle')}`,
        activity: `${t('readingLessons.title')} - ${t('readingLessons.recentActivityTitle')}`,
      },
      readingAreasOfWeakness: {
        activity: `${t('readingAreasOfWeakness.title')} - ${t('readingAreasOfWeakness.recentActivityTitle')}`,
      },
      booksRead: {
        progress: `${t('booksRead.title')} - ${t('booksRead.progressTitle')}`,
        activity: `${t('booksRead.title')} - ${t('booksRead.recentActivityTitle')}`,
      },
      spellingLessons: {
        progress: `${t('spellingLessons.title')} - ${t('spellingLessons.progressTitle')}`,
        placement: `${t('spellingLessons.title')} - ${t('spellingLessons.placementTestsTitle')}`,
        activity: `${t('spellingLessons.title')} - ${t('spellingLessons.recentActivityTitle')}`,
      },
      certificatesEarned: {
        progress: `${t('certificatesEarned.title')} - ${t('certificatesEarned.progressTitle')}`,
      },
      mapQuizzes: {
        progress: `${t('mapQuizzes.title')} - ${t('mapQuizzes.progressTitle')}`,
        activity: `${t('mapQuizzes.title')} - ${t('mapQuizzes.recentActivityTitle')}`,
      },
      drivingTests: {
        progress: `${t('drivingTests.title')} - ${t('drivingTests.progressTitle')}`,
        activity: `${t('drivingTests.title')} - ${t('drivingTests.recentActivityTitle')}`,
      },
      storylands: {
        progress: `${t('storylands.title')} - ${t('storylands.progressTitle')}`,
        activity: `${t('storylands.title')} - ${t('storylands.recentActivityTitle')}`,
      },
      storiesWritten: {
        activity: `${t('storiesWritten.title')} - ${t('storiesWritten.recentActivityTitle')}`,
      },
      assignments: {
        activity: `${t('assignments.title')} - ${t('assignments.recentActivityTitle')}`,
      },
    }
  }

  <template>
    <ReportingTeacherUiHeader @title={{this.pageTitle}} />

    <ReportingStudentSummaryControls
      @students={{@model.students}}
      @student={{@model.student}}
      @routePrefix="reporting.ui-scope.re.student-summary"
      @exportOptions={{this.exportOptions}}
      @isDataPresent={{Boolean @model.students}}
      @studentRouteName="reporting.ui-scope.re.student-summary.table"
      @allStudentsRouteName="reporting.ui-scope.re.dashboard.table"
    />

    <div class="mx-auto flex max-w-(--breakpoint-lg) flex-wrap print:block">
      <div class="w-full lg:w-1/2 print:w-full">
        {{! Lexile Level and Growth }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="lexile-level"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.lexileLevel.title"}}
          @icon="phosphor-chart-line-bold"
        >
          {{! Section header }}
          <div class="border-oceany-blue-150 border-b-4 print:border-black"></div>
          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.lexileGrowth.progress}}
            @columns={{this.lexileGrowthData.columns}}
            @rows={{this.lexileGrowthData.rows}}
            as |bt|
          >
            <bt.head class="bg-oceany-blue-50" as |h|>
              <h.row as |r|>
                <r.cell
                  class="border-r border-white p-3 text-center font-semibold print:border-r-0 print:border-b print:border-black"
                />
              </h.row>
            </bt.head>
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>
        </StudentDashboardTile>

        {{! Reading Lessons }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="reading-lessons"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.readingLessons.title"}}
          @icon="phosphor-share-network-fill"
          @detailedStudentReportRoute="reporting.ui-scope.re.student-summary.course-progress.reading"
        >
          {{! Section header }}
          <div class="border-oceany-blue-150 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.re.studentSummary.readingLessons.progressTitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.readingLessons.progress}}
            @columns={{this.readingLessonsProgressData.columns}}
            @rows={{this.readingLessonsProgressData.rows}}
            as |bt|
          >
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.readingLessons.placementTestsTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.readingLessons.placement}}
            @columns={{this.readingLessonsPlacementTestData.columns}}
            @rows={{this.readingLessonsPlacementTestData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.readingLessons.recentActivityTitle"}}</span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">
              {{t "reporting.re.studentSummary.readingLessons.recentActivitySubtitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.readingLessons.activity}}
            @columns={{this.readingLessonsActivityData.columns}}
            @rows={{this.readingLessonsActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>

        {{! Areas of Weakness (Reading) }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="areas-of-weakness"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.readingAreasOfWeakness.title"}}
          @icon="phosphor-chart-line-bold"
        >
          {{! Section header }}
          <div class="border-oceany-blue-150 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.re.studentSummary.readingAreasOfWeakness.recentActivityTitle"}}
            </span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">
              {{t "reporting.re.studentSummary.readingAreasOfWeakness.recentActivitySubtitle"}}
            </span>
          </div>
          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.readingAreasOfWeakness.activity}}
            @columns={{this.readingAreasOfWeaknessRecentActivityData.columns}}
            @rows={{this.readingAreasOfWeaknessRecentActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>

        {{! Books Read }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="books-read"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.booksRead.title"}}
          @icon="phosphor-book-bookmark-fill"
        >
          {{! Section header }}
          <div class="border-oceany-blue-150 border-b-4 print:border-black"></div>
          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.booksRead.progress}}
            @columns={{this.booksReadProgressData.columns}}
            @rows={{this.booksReadProgressData.rows}}
            as |bt|
          >
            <bt.head class="bg-oceany-blue-50" as |h|>
              <h.row as |r|>
                <r.cell
                  class="border-r border-white p-3 text-center font-semibold print:border-r-0 print:border-b print:border-black"
                />
              </h.row>
            </bt.head>
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.booksRead.recentActivityTitle"}}</span>
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.booksRead.recentActivitySubtitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.booksRead.activity}}
            @columns={{this.booksReadActivityData.columns}}
            @rows={{this.booksReadActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>

        {{! Spelling Lessons }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="spelling-lessons"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.spellingLessons.title"}}
          @icon="phosphor-shooting-star-fill"
          @detailedStudentReportRoute="reporting.ui-scope.re.student-summary.course-progress.spelling"
        >
          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.spellingLessons.progressTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.spellingLessons.progress}}
            @columns={{this.spellingLessonsProgressData.columns}}
            @rows={{this.spellingLessonsProgressData.rows}}
            as |bt|
          >
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.spellingLessons.placementTestsTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.spellingLessons.placement}}
            @columns={{this.spellingLessonsPlacementTestData.columns}}
            @rows={{this.spellingLessonsPlacementTestData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.spellingLessons.recentActivityTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.spellingLessons.activity}}
            @columns={{this.spellingLessonsActivityData.columns}}
            @rows={{this.spellingLessonsActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>
      </div>

      <div class="w-full lg:w-1/2 print:w-full">
        {{! Certificates Earned }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="certificates-earned"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.certificatesEarned.title"}}
          @icon="phosphor-trophy-fill"
        >
          {{! Section header }}
          <div class="border-oceany-blue-150 border-b-4 print:border-black"></div>
          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.certificatesEarned.progress}}
            @columns={{this.certificatesEarnedData.columns}}
            @rows={{this.certificatesEarnedData.rows}}
            as |bt|
          >
            <bt.head class="bg-oceany-blue-50" as |h|>
              <h.row as |r|>
                <r.cell
                  class="w-1/3 border-r border-white p-3 text-center font-semibold print:border-r-0 print:border-b print:border-black"
                />
              </h.row>
            </bt.head>
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>
        </StudentDashboardTile>

        {{! Map Quizzes }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="map-quizzes"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.mapQuizzes.title"}}
          @icon="phosphor-map-trifold-fill"
        >
          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.mapQuizzes.progressTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.mapQuizzes.progress}}
            @columns={{this.mapQuizzesProgressData.columns}}
            @rows={{this.mapQuizzesProgressData.rows}}
            as |bt|
          >
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.mapQuizzes.recentActivityTitle"}}</span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">{{t
                "reporting.re.studentSummary.mapQuizzes.recentActivitySubtitle"
              }}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.mapQuizzes.activity}}
            @columns={{this.mapQuizzesRecentActivityData.columns}}
            @rows={{this.mapQuizzesRecentActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>

        {{! Driving Tests }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="driving-tests"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.drivingTests.title"}}
          @icon="phosphor-flag-checkered-fill"
          @detailedStudentReportRoute="reporting.ui-scope.re.student-summary.driving-tests"
        >
          {{! Section header }}
          <div class="border-oceany-blue-150 border-b-4 print:border-black"></div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.drivingTests.progress}}
            @columns={{this.drivingTestsProgressData.columns}}
            @rows={{this.drivingTestsProgressData.rows}}
            as |bt|
          >
            <bt.head class="bg-oceany-blue-50" as |h|>
              <h.row as |r|>
                <r.cell
                  class="w-1/4 border-r border-white p-3 text-center font-semibold print:border-r-0 print:border-b print:border-black"
                />
              </h.row>
            </bt.head>
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.drivingTests.recentActivityTitle"}}</span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">{{t
                "reporting.re.studentSummary.drivingTests.recentActivitySubtitle"
              }}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.drivingTests.activity}}
            @columns={{this.drivingTestsActivityData.columns}}
            @rows={{this.drivingTestsActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>

        {{! Storylands }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="storylands"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.storylands.title"}}
          @icon="phosphor-share-network-fill"
        >
          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.storylands.progressTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.storylands.progress}}
            @columns={{this.storylandsProgressData.columns}}
            @rows={{this.storylandsProgressData.rows}}
            as |bt|
          >
            <bt.body as |b|>
              <b.row class="border-oceany-blue-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div
            class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
          >
            <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.storylands.recentActivityTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            class="w-full"
            exportable={{this.exportTitles.storylands.activity}}
            @columns={{this.storylandsRecentActivityData.columns}}
            @rows={{this.storylandsRecentActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-oceany-blue-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>

        {{! Stories Written }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="stories-written"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.storiesWritten.title"}}
          @icon="phosphor-book-fill"
          @detailedStudentReportRoute={{if
            this.hasStoriesWrittenActivityData
            "reporting.ui-scope.re.student-summary.stories-written"
          }}
        >
          {{#if this.hasStoriesWrittenActivityData}}
            {{! Section header }}
            <div
              class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
            >
              <span class="px-3 pt-6 pb-3">{{t "reporting.re.studentSummary.storiesWritten.recentActivityTitle"}}</span>
            </div>

            {{! Table }}
            <BasicTable
              class="w-full"
              @columns={{this.storiesWrittenActivityData.columns}}
              @rows={{this.storiesWrittenActivityData.rows}}
              as |bt rows|
            >
              {{#if rows}}
                <bt.body as |b|>
                  <b.row class="border-oceany-blue-150 border-b print:border-black" as |r tableMeta|>
                    {{#if tableMeta.cellObject.storyDownloadUrl}}
                      <r.cell>
                        <a
                          href={{tableMeta.cellObject.storyDownloadUrl}}
                          target="_blank"
                          rel="noreferrer noopener"
                          class="student-panel-link reading print:hidden"
                        >
                          <PhosphorIcon @icon="phosphor-download-fill" />
                          {{tableMeta.cellObject.storyDownloadText}}
                        </a>
                      </r.cell>
                    {{else}}
                      <r.cell />
                    {{/if}}
                  </b.row>
                </bt.body>
              {{else}}
                <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                  {{this.emptyText}}
                </div>
              {{/if}}
            </BasicTable>
            <BasicTable
              class="hidden"
              exportable={{this.exportTitles.storiesWritten.activity}}
              @columns={{this.storiesWrittenActivityData.columns}}
              @rows={{this.storiesWrittenActivityData.rows}}
              as |bt rows|
            >
              {{#if rows}}
                <bt.body />
              {{/if}}
            </BasicTable>
          {{else}}
            <NoDataMessage @compact={{true}} />
          {{/if}}
        </StudentDashboardTile>

        {{! Assignments }}
        <StudentDashboardTile
          data-test-student-dashboard-tile="assignments"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.re.studentSummary.assignments.title"}}
          @icon="phosphor-book-fill"
        >
          {{#if this.hasAssignmentsData}}
            {{! Section header }}
            <div
              class="border-oceany-blue-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black"
            >
              <span class="px-3 pt-6 pb-3">
                {{t "reporting.re.studentSummary.assignments.recentActivityTitle"}}
              </span>
              <span class="w-24 px-3 pt-6 pb-3 text-center">
                {{t "reporting.re.studentSummary.assignments.recentActivitySubtitle"}}
              </span>
            </div>

            {{! Table }}
            <BasicTable
              class="w-full"
              exportable={{this.exportTitles.assignments.activity}}
              @columns={{this.assignmentsData.columns}}
              @rows={{this.assignmentsData.rows}}
              as |bt rows|
            >
              {{#if rows}}
                <bt.body as |b|>
                  <b.row class="border-oceany-blue-150 border-b print:border-black" />
                </bt.body>
              {{else}}
                <div class="border-oceany-blue-150 border-b p-4 print:border-black">
                  {{this.emptyText}}
                </div>
              {{/if}}
            </BasicTable>
          {{else}}
            <NoDataMessage @compact={{true}} />
          {{/if}}
        </StudentDashboardTile>
      </div>
    </div>
  </template>
}

export default RouteTemplate(ReStudentSummaryTablePage)
